(() => {
  const runtime = window.__cpaLandingRuntime || {
    bound: false,
    states: new WeakMap(),
  };

  window.__cpaLandingRuntime = runtime;

  const rootsWithin = (scope = document) => {
    const roots = [];

    if (scope?.matches?.('[data-cpa-landing]')) roots.push(scope);
    scope?.querySelectorAll?.('[data-cpa-landing]').forEach((root) => roots.push(root));

    return roots;
  };

  const restoreInlineStyle = (element, property, previousValue, previousPriority) => {
    if (previousValue) {
      element.style.setProperty(property, previousValue, previousPriority);
    } else {
      element.style.removeProperty(property);
    }
  };

  const initProductForms = (root, signal, cleanupTasks) => {
    const productForms = Array.from(root.querySelectorAll('[data-cpa-product-form]'));

    productForms.forEach((productForm) => {
      const submitButton = productForm.querySelector('[ref="addToCartButton"], button[type="submit"]');
      if (!submitButton) return;

      let updateSequence = 0;
      let temporarilyDisabled = false;

      productForm.addEventListener(
        'shopify:cart:lines-update',
        (event) => {
          const currentSequence = ++updateSequence;
          const wasDisabled = submitButton.disabled;

          temporarilyDisabled = !wasDisabled;
          submitButton.disabled = true;
          submitButton.setAttribute('aria-busy', 'true');

          const resetButton = () => {
            if (currentSequence !== updateSequence) return;

            submitButton.removeAttribute('aria-busy');
            if (!wasDisabled) submitButton.disabled = false;
            temporarilyDisabled = false;
          };

          Promise.resolve(event.promise).then(resetButton, resetButton);
        },
        { signal }
      );

      cleanupTasks.push(() => {
        updateSequence += 1;
        submitButton.removeAttribute('aria-busy');
        if (temporarilyDisabled) submitButton.disabled = false;
      });
    });
  };

  const initProductGallery = (root, signal, cleanupTasks) => {
    const gallery = root.querySelector('[data-cpa-product-gallery]');
    if (!gallery) return;

    const track = gallery.querySelector('[data-cpa-gallery-track]');
    const slides = Array.from(gallery.querySelectorAll('[data-cpa-gallery-slide]'));
    const thumbnails = Array.from(gallery.querySelectorAll('[data-cpa-gallery-thumb]'));
    const thumbnailTrack = gallery.querySelector('[data-cpa-gallery-thumbs]');
    const previousButton = gallery.querySelector('[data-cpa-gallery-previous]');
    const nextButton = gallery.querySelector('[data-cpa-gallery-next]');
    const status = gallery.querySelector('[data-cpa-gallery-status]');
    const zoomButtons = Array.from(gallery.querySelectorAll('[data-cpa-gallery-zoom]'));
    const zoomDialog = gallery.querySelector('[data-cpa-gallery-dialog]');
    const zoomDialogImage = zoomDialog?.querySelector('[data-cpa-gallery-dialog-image]');
    const zoomDialogClose = zoomDialog?.querySelector('[data-cpa-gallery-dialog-close]');

    if (zoomDialog && zoomDialogImage) {
      zoomButtons.forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            zoomDialogImage.src = button.dataset.cpaGalleryZoomSrc || '';
            zoomDialogImage.alt = button.dataset.cpaGalleryZoomAlt || '';

            if (typeof zoomDialog.showModal === 'function') {
              zoomDialog.showModal();
            } else {
              zoomDialog.setAttribute('open', '');
            }
            zoomDialogClose?.focus();
          },
          { signal }
        );
      });

      zoomDialogClose?.addEventListener('click', () => zoomDialog.close(), { signal });
      zoomDialog.addEventListener(
        'click',
        (event) => {
          if (event.target === zoomDialog) zoomDialog.close();
        },
        { signal }
      );

      cleanupTasks.push(() => {
        if (zoomDialog.open) zoomDialog.close();
        zoomDialogImage.removeAttribute('src');
        zoomDialogImage.alt = '';
      });
    }

    if (!track || slides.length < 2) return;

    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    let activeIndex = 0;
    let scrollFrame = 0;

    const syncState = (nextIndex) => {
      const index = Math.max(0, Math.min(slides.length - 1, nextIndex));
      activeIndex = index;

      thumbnails.forEach((thumbnail, thumbnailIndex) => {
        thumbnail.setAttribute('aria-current', thumbnailIndex === index ? 'true' : 'false');
      });

      if (status) status.textContent = `${index + 1} / ${slides.length}`;
      if (previousButton) previousButton.disabled = index === 0;
      if (nextButton) nextButton.disabled = index === slides.length - 1;

      const activeThumbnail = thumbnails[index];
      if (thumbnailTrack && activeThumbnail) {
        const thumbnailStart = activeThumbnail.offsetLeft;
        const thumbnailEnd = thumbnailStart + activeThumbnail.offsetWidth;
        const visibleStart = thumbnailTrack.scrollLeft;
        const visibleEnd = visibleStart + thumbnailTrack.clientWidth;

        if (thumbnailStart < visibleStart || thumbnailEnd > visibleEnd) {
          thumbnailTrack.scrollTo({
            left: thumbnailStart - (thumbnailTrack.clientWidth - activeThumbnail.offsetWidth) / 2,
            behavior: reducedMotion?.matches ? 'auto' : 'smooth',
          });
        }
      }
    };

    const showSlide = (nextIndex) => {
      const index = Math.max(0, Math.min(slides.length - 1, nextIndex));
      const slide = slides[index];
      if (!slide) return;

      syncState(index);
      track.scrollTo({
        left: slide.offsetLeft,
        behavior: reducedMotion?.matches ? 'auto' : 'smooth',
      });
    };

    thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => showSlide(index), { signal });
    });

    previousButton?.addEventListener('click', () => showSlide(activeIndex - 1), { signal });
    nextButton?.addEventListener('click', () => showSlide(activeIndex + 1), { signal });

    track.addEventListener(
      'keydown',
      (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();

        if (event.key === 'ArrowLeft') showSlide(activeIndex - 1);
        if (event.key === 'ArrowRight') showSlide(activeIndex + 1);
        if (event.key === 'Home') showSlide(0);
        if (event.key === 'End') showSlide(slides.length - 1);
      },
      { signal }
    );

    let resizeFrame = 0;
    const realignActiveSlide = () => {
      if (resizeFrame) cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => {
        const activeSlide = slides[activeIndex];
        if (activeSlide) track.scrollTo({ left: activeSlide.offsetLeft, behavior: 'auto' });
      });
    };

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(realignActiveSlide);
      resizeObserver.observe(track);
      cleanupTasks.push(() => resizeObserver.disconnect());
    } else {
      window.addEventListener('resize', realignActiveSlide, { passive: true, signal });
    }

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          const visibleSlide = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
          if (!visibleSlide) return;

          const index = Number(visibleSlide.target.dataset.cpaGalleryIndex);
          if (Number.isInteger(index)) syncState(index);
        },
        { root: track, threshold: [0.6, 0.85] }
      );

      slides.forEach((slide) => observer.observe(slide));
      cleanupTasks.push(() => observer.disconnect());
    } else {
      track.addEventListener(
        'scroll',
        () => {
          if (scrollFrame) cancelAnimationFrame(scrollFrame);
          scrollFrame = requestAnimationFrame(() => {
            const nearestIndex = slides.reduce((bestIndex, slide, index) => {
              const bestDistance = Math.abs(slides[bestIndex].offsetLeft - track.scrollLeft);
              const nextDistance = Math.abs(slide.offsetLeft - track.scrollLeft);
              return nextDistance < bestDistance ? index : bestIndex;
            }, 0);
            syncState(nearestIndex);
          });
        },
        { passive: true, signal }
      );
    }

    syncState(0);
    cleanupTasks.push(() => {
      if (scrollFrame) cancelAnimationFrame(scrollFrame);
      if (resizeFrame) cancelAnimationFrame(resizeFrame);
    });
  };

  const neutralizeEditorPositioning = (root, cleanupTasks) => {
    if (!window.Shopify?.designMode) return;

    const positionedElements = root.querySelectorAll('[data-cpa-sticky]');

    positionedElements.forEach((element) => {
      const previousValue = element.style.getPropertyValue('position');
      const previousPriority = element.style.getPropertyPriority('position');
      element.style.setProperty('position', 'static', 'important');
      cleanupTasks.push(() => {
        restoreInlineStyle(element, 'position', previousValue, previousPriority);
      });
    });
  };

  const initCpaLanding = (root) => {
    if (!root || runtime.states.has(root)) return runtime.states.get(root);

    const controller = new AbortController();
    const cleanupTasks = [];
    const state = {
      controller,
      cleanup() {
        controller.abort();
        cleanupTasks.splice(0).forEach((cleanup) => cleanup());
        runtime.states.delete(root);
        delete root.dataset.cpaReady;
      },
    };

    runtime.states.set(root, state);
    root.dataset.cpaReady = 'true';

    initProductGallery(root, controller.signal, cleanupTasks);
    initProductForms(root, controller.signal, cleanupTasks);
    neutralizeEditorPositioning(root, cleanupTasks);

    return state;
  };

  const syncSectionLinks = () => {
    document.querySelectorAll('[data-cpa-section-link][href^="#"]').forEach((link) => {
      const targetId = link.getAttribute('href')?.slice(1);
      link.hidden = !targetId || !document.getElementById(targetId);
    });
  };

  const initAllCpaLandings = (scope = document) => {
    rootsWithin(scope).forEach(initCpaLanding);
    syncSectionLinks();
  };

  const cleanupAllCpaLandings = (scope) => {
    rootsWithin(scope).forEach((root) => runtime.states.get(root)?.cleanup());
  };

  runtime.initAll = initAllCpaLandings;

  if (runtime.bound) {
    runtime.initAll(document);
    return;
  }

  runtime.bound = true;
  const lifecycleController = new AbortController();
  const lifecycleSignal = lifecycleController.signal;
  runtime.lifecycleController = lifecycleController;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllCpaLandings(), {
      once: true,
      signal: lifecycleSignal,
    });
  } else {
    initAllCpaLandings();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => initAllCpaLandings(event.target),
    { signal: lifecycleSignal }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      cleanupAllCpaLandings(event.target);
      requestAnimationFrame(syncSectionLinks);
    },
    { signal: lifecycleSignal }
  );

})();
